import React, { useState } from "react";
import { contact } from "../mock";
import { Phone, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact({ source = "" }) {
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/loyaris/contact`, { ...form, source: source || window.location.pathname });
      setSent(true);
      toast({ title: "Message envoyé", description: "Notre équipe vous recontacte très vite." });
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
    } catch (err) {
      const detail = err.response?.status === 429 ? err.response.data.detail : "Merci de réessayer dans un instant ou de nous écrire à contact@loyaris.fr.";
      toast({ title: "Envoi impossible", description: detail, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#26241F] py-20 lg:py-24">
      <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
          <div>
            <p className="mb-3 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#B08D57]">
              <span className="h-px w-8 bg-[#B08D57]" /> {contact.eyebrow}
            </p>
            <h2 className="font-serifDisplay text-[34px] leading-tight text-white lg:text-[46px]">
              {contact.title}
            </h2>
            <p className="mt-4 max-w-md text-[16px] leading-relaxed text-white/70">{contact.text}</p>

            <div className="mt-9 space-y-4">
              <a href={`tel:${contact.phoneRaw}`} className="group flex items-center gap-4 rounded-xl border border-white/15 bg-white/5 p-4 transition-colors hover:border-[#B08D57]">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#B08D57] text-[#26241F]">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-[13px] text-white/60">Vous préférez nous appeler ?</div>
                  <div className="text-[18px] font-semibold text-white">{contact.phone}</div>
                </div>
              </a>
              <div className="flex items-center gap-4 rounded-xl border border-white/15 bg-white/5 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-[#B08D57]">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-[13px] text-white/60">Notre agence</div>
                  <div className="text-[16px] font-semibold text-white">{contact.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-[#FBF9F5] p-7 lg:p-9">
            {sent ? (
              <div data-testid="contact-success" className="flex h-full flex-col items-center justify-center py-14 text-center">
                <CheckCircle2 className="h-14 w-14 text-[#B08D57]" />
                <h3 className="mt-4 font-serifDisplay text-[26px] text-[#26241F]">Merci !</h3>
                <p className="mt-2 max-w-sm text-[15px] text-[#6e6a62]">
                  Votre demande a bien été enregistrée. Notre équipe vous recontacte dans les plus brefs délais.
                </p>
                <button onClick={() => setSent(false)} className="mt-6 text-[14px] font-semibold text-[#B08D57] hover:underline">
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <input type="text" name="website" value={form.website} onChange={handleChange} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
                <h3 className="text-[20px] font-semibold text-[#26241F]">Confier mon bien</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-[#403d35]">Nom complet</label>
                    <input name="name" data-testid="contact-name-input" required value={form.name} onChange={handleChange}
                      className="w-full rounded-lg border border-[#ded5c4] bg-white px-4 py-3 text-[15px] text-[#26241F] outline-none transition-colors focus:border-[#B08D57]"
                      placeholder="Jean Dupont" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-[#403d35]">Téléphone</label>
                    <input name="phone" data-testid="contact-phone-input" value={form.phone} onChange={handleChange}
                      className="w-full rounded-lg border border-[#ded5c4] bg-white px-4 py-3 text-[15px] text-[#26241F] outline-none transition-colors focus:border-[#B08D57]"
                      placeholder="06 12 34 56 78" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-[#403d35]">Email</label>
                  <input name="email" data-testid="contact-email-input" type="email" required value={form.email} onChange={handleChange}
                    className="w-full rounded-lg border border-[#ded5c4] bg-white px-4 py-3 text-[15px] text-[#26241F] outline-none transition-colors focus:border-[#B08D57]"
                    placeholder="jean.dupont@email.com" />
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-[#403d35]">Votre projet</label>
                  <textarea name="message" data-testid="contact-message-input" rows={4} value={form.message} onChange={handleChange}
                    className="w-full resize-none rounded-lg border border-[#ded5c4] bg-white px-4 py-3 text-[15px] text-[#26241F] outline-none transition-colors focus:border-[#B08D57]"
                    placeholder="Décrivez votre bien et vos attentes..." />
                </div>
                <button type="submit" data-testid="contact-submit-button" disabled={sending} className="group flex w-full disabled:opacity-60 items-center justify-center gap-2 rounded-md bg-[#26241F] px-6 py-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#B08D57] hover:text-[#26241F]">
                  {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <p className="text-center text-[12px] text-[#9a9488]">
                  Vos informations sont uniquement utilisées pour répondre à votre demande. <a href="/confidentialite" className="underline hover:text-[#26241F]">En savoir plus</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
